// /reports/templates — list view of saved Custom Report templates.
// Per-row actions: Open / Run / Edit / Delete. Edit and Delete are disabled
// for non-creators (admins are allowed by the API).

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { useMe } from '@/hooks/useMe';
import { ApiError, reports } from '@/lib/api';

export function TemplatesPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['reports', 'templates'],
    queryFn: () => reports.templates.list(),
  });
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => reports.templates.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reports', 'templates'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const run = useMutation({
    mutationFn: (id: number) => reports.templates.runExport(id),
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Report Templates</h1>
        <Link to="/reports/builder" className="text-sm text-brand-700 hover:underline">
          + New custom report
        </Link>
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <Card>
        <CardHeader>
          <CardTitle>Saved templates</CardTitle>
        </CardHeader>
        <CardBody className="overflow-auto">
          {q.isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : q.error ? (
            <div className="text-sm text-red-600">{(q.error as Error).message}</div>
          ) : q.data && q.data.data.length === 0 ? (
            <div className="text-sm text-slate-500">
              No saved templates yet. Build one in the{' '}
              <Link to="/reports/builder" className="underline">
                Custom Report Builder
              </Link>
              .
            </div>
          ) : q.data ? (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-1 pl-1 text-left">Name</th>
                  <th className="py-1 text-left">Balance source</th>
                  <th className="py-1 text-left">Creator</th>
                  <th className="py-1 text-left">Last updated</th>
                  <th className="py-1 pr-1 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {q.data.data.map((t) => {
                  const ownedByMe = me.data?.id === t.created_by_user_id;
                  const canEdit = ownedByMe || me.data?.role === 'admin';
                  return (
                    <tr key={t.id}>
                      <td className="py-1 pl-1">
                        <div className="font-medium">{t.name}</div>
                        {t.description && (
                          <div className="text-xs text-slate-500">{t.description}</div>
                        )}
                      </td>
                      <td className="py-1 text-slate-600">
                        {t.balance_source}
                        {t.default_as_of_period && (
                          <span className="ml-1 text-xs text-slate-400">@ {t.default_as_of_period}</span>
                        )}
                      </td>
                      <td className="py-1 text-slate-600">{t.created_by_username ?? '—'}</td>
                      <td className="py-1 text-slate-600 text-xs">
                        {(t.updated_at ?? t.created_at).slice(0, 19).replace('T', ' ')}
                      </td>
                      <td className="py-1 pr-1 text-right space-x-1">
                        <Link
                          to="/reports/builder"
                          search={{ template: t.id }}
                          className="text-xs underline"
                        >
                          Open
                        </Link>
                        <button
                          onClick={() => run.mutate(t.id)}
                          className="text-xs text-slate-700 hover:text-slate-900 underline"
                        >
                          Run
                        </button>
                        <Link
                          to="/reports/builder"
                          search={{ template: t.id }}
                          className={
                            canEdit ? 'text-xs underline' : 'text-xs text-slate-300 pointer-events-none'
                          }
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete template "${t.name}"?`)) remove.mutate(t.id);
                          }}
                          disabled={!canEdit}
                          className={
                            canEdit ? 'text-xs text-red-600 hover:underline' : 'text-xs text-slate-300'
                          }
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

// Use the `me` hook to detect ownership above; export the page.
// Re-export for the route-tree (and to silence "no exports" warnings if any).
export const _useMe = useMe;
